import { MAPTILER_API_KEY, ABEOKUTA_CENTER } from './maptilerConfig';
import { distanceMeters, type LatLng } from './locationSmoothing';

/** Abeokuta / Ogun service envelope (same idea as go7 MAPTILER_BBOX) */
const SERVICE_BBOX = {
  minLng: 2.92,
  minLat: 6.9,
  maxLng: 3.88,
  maxLat: 7.58,
};

export function isInServiceArea(point?: LatLng | null): boolean {
  if (!point) return false;
  return (
    point.lat >= SERVICE_BBOX.minLat &&
    point.lat <= SERVICE_BBOX.maxLat &&
    point.lng >= SERVICE_BBOX.minLng &&
    point.lng <= SERVICE_BBOX.maxLng
  );
}

/**
 * True when stored coords look wrong for a local Abeokuta delivery
 * (e.g. Oke Lantoro stored as Oke Ogun at lat 6.98 instead of ~7.15).
 */
export function looksMisplacedDestination(
  point: LatLng | null | undefined,
  address?: string
): boolean {
  if (!point) return Boolean(address);
  if (!isInServiceArea(point)) return true;
  const fromCenter = distanceMeters(point, ABEOKUTA_CENTER);
  if (fromCenter > 45_000) return true;
  const text = (address || '').toLowerCase();
  if (
    /(lantoro|abeokuta|kuto|isabo|ibara|sapon|adatan|eleweran)/i.test(text) &&
    point.lat < 7.05
  ) {
    return true;
  }
  return false;
}

function scoreGeocodeHit(feature: any, address: string): number {
  const name = String(feature?.place_name || feature?.text || '').toLowerCase();
  const [lng, lat] = feature?.center || feature?.geometry?.coordinates || [];
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return -1;
  if (lat < 7.05) return -1; // reject Oke Ogun / south false matches for Abeokuta jobs
  let score = 0;
  if (name.includes('abeokuta')) score += 5;
  const tokens = address
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 3);
  for (const t of tokens) {
    if (name.includes(t)) score += 3;
  }
  score += Math.max(0, 4 - distanceMeters({ lat, lng }, ABEOKUTA_CENTER) / 5000);
  return score;
}

async function maptilerGeocode(query: string): Promise<LatLng | null> {
  const url =
    `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json` +
    `?key=${MAPTILER_API_KEY}&limit=8&language=en` +
    `&proximity=${ABEOKUTA_CENTER.lng},${ABEOKUTA_CENTER.lat}` +
    `&bbox=${SERVICE_BBOX.minLng},${SERVICE_BBOX.minLat},${SERVICE_BBOX.maxLng},${SERVICE_BBOX.maxLat}`;

  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const features: any[] = data?.features || [];
  let best: LatLng | null = null;
  let bestScore = 0;
  for (const f of features) {
    const s = scoreGeocodeHit(f, query);
    if (s > bestScore) {
      const [lng, lat] = f.center || f.geometry?.coordinates || [];
      best = { lat, lng };
      bestScore = s;
    }
  }
  return bestScore > 0 ? best : null;
}

async function googleGeocode(address: string): Promise<LatLng | null> {
  const google = (window as any).google;
  if (!google?.maps?.Geocoder) return null;
  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { address: `${address}, Abeokuta, Nigeria`, region: 'ng' },
      (results: any[], status: string) => {
        if (status !== 'OK' || !results?.length) {
          resolve(null);
          return;
        }
        for (const r of results) {
          const loc = r.geometry?.location;
          if (!loc) continue;
          const point = { lat: loc.lat(), lng: loc.lng() };
          if (point.lat >= 7.05 && isInServiceArea(point)) {
            resolve(point);
            return;
          }
        }
        resolve(null);
      }
    );
  });
}

/** Forward geocode biased to Abeokuta — used when job coords are junk. */
export async function geocodeInAbeokuta(address: string): Promise<LatLng | null> {
  const q = address.trim();
  if (!q) return null;

  const placeHint =
    q.match(/\b(oke\s*lantoro|lantoro|kuto|isabo|ibara|sapon|adatan|eleweran)\b/i)?.[0] || q;

  const queries = [
    `${placeHint}, Abeokuta, Ogun, Nigeria`,
    /abeokuta|ogun/i.test(q) ? q : `${q}, Abeokuta, Ogun, Nigeria`,
  ];

  const seen = new Set<string>();
  for (const query of queries) {
    const key = query.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    try {
      const g = await googleGeocode(query);
      if (g) return g;
    } catch {
      /* continue */
    }
    try {
      const m = await maptilerGeocode(query);
      if (m) return m;
    } catch {
      /* continue */
    }
  }
  return null;
}
